import { useState, useMemo } from "react";
import { useLang } from "@/context/LangContext";
import { Sliders, ToggleLeft, ToggleRight, Clock, Zap, TrendingDown, TrendingUp, RotateCcw } from "lucide-react";

export default function ScopeEditor({ analysis, hourlyRate = 0 }) {
  const { t } = useLang();
  const allElements = analysis?.visualElements || [];
  const [excluded, setExcluded] = useState(new Set());

  const toggle = (name) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const resetAll = () => setExcluded(new Set());

  const included = useMemo(() => allElements.filter(e => !excluded.has(e.name)), [allElements, excluded]);
  const origMin = allElements.reduce((s, e) => s + (e.hours_min || 0), 0);
  const origMax = allElements.reduce((s, e) => s + (e.hours_max || 0), 0);
  const newMin = included.reduce((s, e) => s + (e.hours_min || 0), 0);
  const newMax = included.reduce((s, e) => s + (e.hours_max || 0), 0);
  const savedMin = origMin - newMin;
  const savedMax = origMax - newMax;
  const rate = parseFloat(hourlyRate) || 85;
  const origCost = ((origMin + origMax) / 2) * rate;
  const newCost = ((newMin + newMax) / 2) * rate;
  const savedCost = origCost - newCost;
  const hasChanges = excluded.size > 0;

  if (!allElements.length) return null;

  return (
    <div data-testid="scope-editor" className="glass-surface rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sliders className="w-4 h-4 text-[#00F0FF]" />
          <h3 className="font-['Outfit'] text-sm font-semibold text-white">Scope-Editor</h3>
          <span className="text-[10px] font-['JetBrains_Mono'] text-[#606070]">{included.length}/{allElements.length} aktiv</span>
        </div>
        {hasChanges && (
          <button onClick={resetAll} className="flex items-center gap-1.5 text-[10px] text-[#A0A0B0] hover:text-white transition-colors font-['JetBrains_Mono']">
            <RotateCcw className="w-3 h-3" /> Zuruecksetzen
          </button>
        )}
      </div>

      {/* Info */}
      <div className="px-5 py-3 bg-[#0A0A14]/60 border-b border-white/5">
        <p className="text-[11px] text-[#808090]">
          Schalten Sie Komponenten ein/aus um zu sehen, wie sich Kosten und Zeitrahmen aendern. So finden Sie den optimalen Umfang fuer Ihr Budget.
        </p>
      </div>

      {/* Impact Summary */}
      {hasChanges && (
        <div className="px-5 py-3 bg-gradient-to-r from-[#00F0FF]/[0.05] to-[#FFB800]/[0.03] border-b border-white/5">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className="text-[10px] text-[#606070] font-['JetBrains_Mono'] block mb-1">STUNDEN GESPART</span>
              <span className="text-lg font-['JetBrains_Mono'] font-bold text-[#00F0FF]">-{savedMin}-{savedMax}h</span>
              <span className="block text-[10px] text-[#00F0FF]/60 font-['JetBrains_Mono']">≈ {Math.round(savedMin/8)}-{Math.round(savedMax/8)} Tage</span>
            </div>
            <div>
              <span className="text-[10px] text-[#606070] font-['JetBrains_Mono'] block mb-1">KOSTEN GESPART</span>
              <span className="text-lg font-['JetBrains_Mono'] font-bold text-[#00F0FF]">-{(savedCost/1000).toFixed(1)}k EUR</span>
            </div>
            <div>
              <span className="text-[10px] text-[#606070] font-['JetBrains_Mono'] block mb-1">NEUER PREIS</span>
              <span className="text-lg font-['JetBrains_Mono'] font-bold text-white">{(newCost/1000).toFixed(1)}k EUR</span>
            </div>
          </div>
        </div>
      )}

      {/* Component toggles */}
      <div className="divide-y divide-white/5">
        {allElements.map((el, i) => {
          const isActive = !excluded.has(el.name);
          const isPlane = el.type === "Plane";
          const accentColor = isPlane ? "#00F0FF" : "#FFB800";
          return (
            <div
              key={i}
              className={`flex items-center gap-3 px-5 py-3 transition-all cursor-pointer hover:bg-white/[0.02] ${!isActive ? "opacity-40" : ""}`}
              onClick={() => toggle(el.name)}
              data-testid={`scope-toggle-${i}`}
            >
              {/* Toggle */}
              {isActive ? (
                <ToggleRight className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
              ) : (
                <ToggleLeft className="w-5 h-5 flex-shrink-0 text-[#606070]" />
              )}

              {/* Number */}
              <span className="w-6 h-6 flex items-center justify-center rounded-lg text-[10px] font-['JetBrains_Mono'] font-bold flex-shrink-0"
                style={{ background: isActive ? `${accentColor}20` : "rgba(255,255,255,0.05)", color: isActive ? accentColor : "#606070" }}>
                {i + 1}
              </span>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium ${isActive ? "text-white" : "text-[#606070] line-through"}`}>{el.name}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded font-['JetBrains_Mono']"
                    style={{ background: `${accentColor}10`, color: accentColor, opacity: isActive ? 1 : 0.4 }}>
                    {isPlane ? "UI" : "Backend"}
                  </span>
                </div>
                {el.description && (
                  <span className="text-[10px] text-[#606070] block mt-0.5">{el.description}</span>
                )}
              </div>

              {/* Hours */}
              <div className="text-right flex-shrink-0">
                <span className={`text-xs font-['JetBrains_Mono'] ${isActive ? "text-white" : "text-[#606070]"}`}>
                  {el.hours_min || 0}-{el.hours_max || 0}h
                </span>
                <span className={`block text-[9px] font-['JetBrains_Mono'] ${isActive ? "text-[#606070]" : "text-[#606070]/60"}`}>
                  ≈ {Math.round((el.hours_min || 0)/8)}-{Math.round((el.hours_max || 0)/8)} T.
                </span>
                {rate > 0 && (
                  <span className={`block text-[10px] font-['JetBrains_Mono'] ${isActive ? "text-[#A0A0B0]" : "text-[#606070]"}`}>
                    {(((el.hours_min || 0) + (el.hours_max || 0)) / 2 * rate / 1000).toFixed(1)}k EUR
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer summary */}
      <div className="px-5 py-3 border-t border-white/10 bg-[#0A0A14] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-[#A0A0B0]" />
          <span className="text-xs text-[#A0A0B0]">
            {hasChanges ? (
              <><span className="line-through text-[#606070]">{origMin}-{origMax}h</span> <span className="text-white font-bold">{newMin}-{newMax}h</span></>
            ) : (
              <span className="text-white font-['JetBrains_Mono']">{origMin}-{origMax}h</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-[#A0A0B0]" />
          <span className="text-xs text-[#A0A0B0]">
            {hasChanges ? (
              <><span className="line-through text-[#606070]">{(origCost/1000).toFixed(1)}k</span> <span className="text-white font-bold">{(newCost/1000).toFixed(1)}k EUR</span></>
            ) : (
              <span className="text-white font-['JetBrains_Mono']">{(origCost/1000).toFixed(1)}k EUR</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
